import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-invalid", "aria-label", "aria-labelledby", "aria-required", "autoFocus", "calendarPage", "clearLabel", "disabled", "disabledValues", "error", "focusedValue", "format", "iconSize", "Input", "inputRef", "inputWidth", "min", "max", "maxValidationMessage", "minValidationMessage", "name", "onCalendarPageChange", "onChange", "onFocusedValueChange", "onOpenChange", "open", "readOnly", "required", "today", "todayLabel", "use", "value", "onFocus"];
import { useContext, memo } from 'react';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import { FieldsetContext } from '../../context/FieldsetContext';
import { isDateInRange, isValid } from '../../utils/SimpleDate';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { ControllableNext } from '../Controllable/ControllableNext';
import ShareInput from '../ShareInput/ShareInput';
import { TextInput } from '../TextInput/TextInput';
import { getDatePlaceholder, getLocalizedDateFormat } from '../../utils/Locale';
import AbstractCalendar from '../AbstractCalendar/AbstractCalendar';
import { CalendarPagePopover } from '../Dates/CalendarPagePopover';
import { UsesToday } from '../Dates/internal/UsesToday';
import { InputIconWrapper } from '../Dates/internal/InputIconWrapper';
import { DateIcon } from 'foundations-assets';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import I18n from 'I18n';
import { jsx as _jsx } from "react/jsx-runtime";
const isValidValue = (value, min, max) => {
  if (value == null) return true;
  if (!isValid(value)) return false;
  return isDateInRange(value, min, max);
};
const DateInputComponent = /*#__PURE__*/memo(props => {
  const {
      'aria-invalid': ariaInvalid,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-required': ariaRequired,
      autoFocus,
      calendarPage,
      clearLabel = I18n.text('ui.datePicker.clear'),
      disabled,
      disabledValues,
      error,
      focusedValue,
      format = 'L',
      iconSize,
      Input = TextInput,
      inputRef,
      inputWidth,
      min,
      // minValue,
      max,
      // maxValue,
      maxValidationMessage = I18n.text('ui.datePicker.dateAboveMax'),
      // maxValueValidationMessage,
      minValidationMessage = I18n.text('ui.datePicker.dateBelowMin'),
      // minValueValidationMessage,
      name,
      onCalendarPageChange,
      onChange,
      onFocusedValueChange: __onFocusedValueChange,
      onOpenChange,
      open = false,
      readOnly,
      required,
      today,
      todayLabel = I18n.text('ui.datePicker.today'),
      use,
      value,
      onFocus
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const calendarId = useUniqueId('dateinput-calendar-');
  const popoverId = useUniqueId('dateinput-popover-');
  const fieldsetContext = useContext(FieldsetContext);
  const computedDisabled = disabled || fieldsetContext.disabled;
  const triggerOpen = () => {
    if (readOnly || computedDisabled) return;
    if (!onOpenChange) return;
    onOpenChange(syntheticEvent(true));
  };
  return /*#__PURE__*/_jsx(AbstractCalendar, {
    calendarPage: calendarPage,
    disabledValues: disabledValues,
    format: format,
    min: min,
    max: max,
    onCalendarPageChange: onCalendarPageChange,
    onChange: onChange,
    onFocusedValueChange: __onFocusedValueChange,
    onOpenChange: onOpenChange,
    open: open,
    today: today,
    value: value,
    children: ({
      onInputChange,
      onKeyDown,
      onKeyUp,
      onBlur,
      inputValue
    }) => {
      return /*#__PURE__*/_jsx(CalendarPagePopover, {
        calendarId: calendarId,
        calendarPage: calendarPage,
        clearLabel: clearLabel,
        disabledValues: disabledValues,
        focusedValue: focusedValue,
        id: popoverId,
        max: max,
        min: min,
        maxValidationMessage: maxValidationMessage,
        minValidationMessage: minValidationMessage,
        onCalendarPageChange: onCalendarPageChange,
        onChange: onChange,
        onFocusLeave: () => {
          if (!onOpenChange) return;
          onOpenChange(syntheticEvent(false));
        },
        onMouseDown: e => {
          e.preventDefault();
        },
        onOpenChange: onOpenChange,
        open: open && !computedDisabled && !readOnly,
        today: today,
        todayLabel: todayLabel,
        value: value,
        children: /*#__PURE__*/_jsx(InputIconWrapper, Object.assign({}, domProps, {
          onFocus: onFocus,
          $cssStyles: cssStyles,
          disabled: computedDisabled,
          icon: DateIcon,
          size: iconSize || fieldsetContext.size,
          children: /*#__PURE__*/_jsx(Input, {
            "aria-invalid": error ? 'true' : ariaInvalid,
            "aria-label": ariaLabel,
            "aria-labelledby": ariaLabelledBy,
            "aria-required": ariaRequired,
            autoComplete: "off",
            autoFocus: autoFocus,
            "data-format": getLocalizedDateFormat(format),
            disabled: computedDisabled,
            error: error || !isValidValue(value, min, max),
            inputRef: inputRef,
            name: name,
            onBlur: onBlur,
            onChange: onInputChange,
            onClick: triggerOpen,
            onFocus: e => {
              if (readOnly || computedDisabled) return;
              e.target.select(); // inputRef is not yet available (see facebook/react#7769)
              triggerOpen();
            },
            onKeyDown: onKeyDown,
            onKeyUp: onKeyUp,
            placeholder: getDatePlaceholder(format),
            readOnly: readOnly,
            required: required,
            use: use,
            value: inputValue,
            inlineSize: inputWidth ? `${inputWidth}px` : undefined
          })
        }))
      });
    }
  });
});
export const DateInputStorybook = DateInputComponent;
export const DateInput = ShareInput(ControllableNext(UsesToday(DateInputComponent), ['calendarPage', 'open', 'value', 'focusedValue']));